import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import {Box, ButtonBase} from "@mui/material";
import {alpha} from "@mui/material/styles";
import React, {ReactNode} from "react";
import {surveyControls} from "../surveyRunnerColors";

const FIELD_HEIGHT = 46;
const STEP_SIZE = 44;
const STEP_INSET = (FIELD_HEIGHT - STEP_SIZE) / 2;

const FIELD_MAX_WIDTH = 320;

interface Props {
    value?: number
    onChange: (value?: number) => void
    min?: number
    max?: number
    placeholder?: string
    ariaLabel?: string
    decreaseLabel: string
    increaseLabel: string
}

interface StepButtonProps {
    label: string
    disabled?: boolean
    onClick: () => void
    children: ReactNode
}

const StepButton = ({label, disabled, onClick, children}: StepButtonProps) => (
    <ButtonBase
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        sx={{
            width: STEP_SIZE,
            height: STEP_SIZE,
            m: `${STEP_INSET}px`,
            flexShrink: 0,
            borderRadius: 1,
            color: 'text.secondary',
            transition: 'background-color 150ms ease, color 150ms ease',
            '&:hover': theme => ({
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                color: theme.palette.primary.main,
            }),
            '&:active': theme => ({backgroundColor: alpha(theme.palette.primary.main, 0.12)}),
            '&.Mui-disabled': {color: 'text.disabled'},
        }}
    >
        {children}
    </ButtonBase>
);

function RunnerNumberField({
    value,
    onChange,
    min,
    max,
    placeholder,
    ariaLabel,
    decreaseLabel,
    increaseLabel,
}: Props) {
    const clamp = (next: number): number => {
        if (min !== undefined && min !== null && next < min) return min;
        if (max !== undefined && max !== null && next > max) return max;
        return next;
    };

    const atFloor = value === undefined || (min !== undefined && min !== null && value <= min);
    const atCeiling = value !== undefined && max !== undefined && max !== null && value >= max;

    return (
        <Box
            sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: FIELD_MAX_WIDTH,
                height: FIELD_HEIGHT,
                borderRadius: 1,
                border: '1px solid',
                borderColor: surveyControls(theme).border,
                backgroundColor: theme.palette.background.paper,
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
                '&:hover': {borderColor: surveyControls(theme).hoverBorder},
                '&:focus-within': {
                    borderColor: surveyControls(theme).focus,
                    boxShadow: `inset 0 0 0 1px ${surveyControls(theme).focus}`,
                },
            })}
        >
            <StepButton
                label={decreaseLabel}
                disabled={atFloor}
                onClick={() => value !== undefined && onChange(clamp(value - 1))}
            >
                <RemoveRoundedIcon sx={{fontSize: 22}}/>
            </StepButton>

            <Box
                component={'input'}
                type={'text'}
                inputMode={'numeric'}
                aria-label={ariaLabel}
                placeholder={placeholder}
                value={value === undefined ? '' : String(value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const next = event.target.value.replace(/[^\d.-]/g, '');
                    const parsed = Number(next);
                    onChange(next === '' || Number.isNaN(parsed) ? undefined : parsed);
                }}
                onBlur={() => {
                    if (value !== undefined) onChange(clamp(value));
                }}
                sx={theme => ({
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    textAlign: 'center',
                    fontFamily: 'inherit',
                    fontSize: 'max(16px, 1rem)',
                    color: theme.palette.text.primary,
                    '&::placeholder': {color: theme.palette.text.secondary, opacity: 1},
                })}
            />

            <StepButton
                label={increaseLabel}
                disabled={atCeiling}
                onClick={() => onChange(value === undefined ? clamp(min ?? 1) : clamp(value + 1))}
            >
                <AddRoundedIcon sx={{fontSize: 22}}/>
            </StepButton>
        </Box>
    );
}

export default RunnerNumberField;
